/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { defaultFilterValues } from "Constants/AzureBenefitConstants";
import { defaultListingPageSize } from "Constants/BenefitConstants";
import { OperationStatus } from "Constants/OperationStatus";

import {
  AzureBenefitRequestsActionTypes,
  IApproveOrDeclineAzureBenefitRequest,
  IApproveOrDeclineAzureBenefitRequestFailure,
  IApproveOrDeclineAzureBenefitRequestSuccess,
  IAzureBenefitRequestsFilterValueAdd,
  IAzureBenefitRequestsFilterValueRemove,
  IAzureBenefitRequestsFilterValueUpdate,
  IGetAzureBenefitRequestDetailsFail,
  IGetAzureBenefitRequestDetailsSuccess,
  ISearchAzureBenefitRequests,
  ISearchAzureBenefitRequestsFail,
  ISearchAzureBenefitRequestsSuccess,
} from "./AzureBenefitRequestsAction";
import {
  AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE,
  AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_FAILURE,
  AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_SUCCESS,
  AZURE_BENEFIT_REQUESTS_FILTER_VALUE_ADD,
  AZURE_BENEFIT_REQUESTS_FILTER_VALUE_CLEAR,
  AZURE_BENEFIT_REQUESTS_FILTER_VALUE_REMOVE,
  AZURE_BENEFIT_REQUESTS_FILTER_VALUE_UPDATE,
  AZURE_BENEFIT_REQUESTS_SEARCH,
  AZURE_BENEFIT_REQUESTS_SEARCH_FAIL,
  AZURE_BENEFIT_REQUESTS_SEARCH_SUCCESS,
  GET_AZURE_BENEFIT_REQUEST_DETAILS,
  GET_AZURE_BENEFIT_REQUEST_DETAILS_FAIL,
  GET_AZURE_BENEFIT_REQUEST_DETAILS_SUCCESS,
} from "./AzureBenefitRequestsActionTypes";
import { IAzureBenefitRequestsState } from "./IAzureBenefitRequestsState";

const INITIAL_STATE: IAzureBenefitRequestsState = {
  currentPage: 1,
  filteredCount: 0,
  isLoadingAzureBenefitRequests: false,
  loadAzureBenefitRequestsError: "",
  pageSize: defaultListingPageSize,
  searchText: "",
  requests: [],
  azureBenefitRequestsFilterValues: defaultFilterValues,
  selectedAzureBenefitRequest: undefined,
  fetchAzureBenefitRequestDetailsErrorMessage: "",
  selectedAzureBenefitRequestIsExpedited: false,
  selectedAzureBenefitRequestExpeditedByDate: undefined,
  azureBenefitApproveOrDeclineOperationStatus: OperationStatus.Idle,
  azureBenefitApproveOrDeclineErrorMessage: "",
};

const applySearchAzureBenefitRequests = (
  state: IAzureBenefitRequestsState,
  action: ISearchAzureBenefitRequests
): IAzureBenefitRequestsState => ({
  ...state,
  requests: [],
  isLoadingAzureBenefitRequests: true,
  searchText: action.searchText,
  currentPage: action.pageIndex,
  loadAzureBenefitRequestsError: "",
  azureBenefitApproveOrDeclineOperationStatus: OperationStatus.Idle,
  azureBenefitApproveOrDeclineErrorMessage: "",
});

const applySearchAzureBenefitRequestsSuccess = (
  state: IAzureBenefitRequestsState,
  action: ISearchAzureBenefitRequestsSuccess
): IAzureBenefitRequestsState => ({
  ...state,
  requests: action.requests,
  currentPage: action.pageIndex,
  isLoadingAzureBenefitRequests: false,
  filteredCount: action.filteredCount,
  loadAzureBenefitRequestsError: "",
});

const applySearchAzureBenefitRequestsFail = (
  state: IAzureBenefitRequestsState,
  action: ISearchAzureBenefitRequestsFail
): IAzureBenefitRequestsState => ({
  ...state,
  loadAzureBenefitRequestsError: action.error,
  isLoadingAzureBenefitRequests: false,
});

const addAzureBenefitRequestsFilterValue = (
  state: IAzureBenefitRequestsState,
  action: IAzureBenefitRequestsFilterValueAdd
): IAzureBenefitRequestsState => ({
  ...state,
  azureBenefitRequestsFilterValues: state.azureBenefitRequestsFilterValues.concat([
    action.filterValue,
  ]),
});

const removeAzureBenefitRequestsFilterValue = (
  state: IAzureBenefitRequestsState,
  action: IAzureBenefitRequestsFilterValueRemove
): IAzureBenefitRequestsState => ({
  ...state,
  azureBenefitRequestsFilterValues: state.azureBenefitRequestsFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const updateAzureBenefitRequestsFilterValue = (
  state: IAzureBenefitRequestsState,
  action: IAzureBenefitRequestsFilterValueUpdate
): IAzureBenefitRequestsState => ({
  ...state,
  azureBenefitRequestsFilterValues: state.azureBenefitRequestsFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

const clearAzureBenefitRequestsFilterValue = (
  state: IAzureBenefitRequestsState
): IAzureBenefitRequestsState => ({
  ...state,
  azureBenefitRequestsFilterValues: [],
});

const applyGetAzureBenefitRequestDetails = (
  state: IAzureBenefitRequestsState
): IAzureBenefitRequestsState => ({
  ...state,
  selectedAzureBenefitRequest: undefined,
  selectedAzureBenefitRequestIsExpedited: false,
  fetchAzureBenefitRequestDetailsErrorMessage: "",
  selectedAzureBenefitRequestExpeditedByDate: undefined,
});

const applyGetAzureBenefitRequestDetailsSuccess = (
  state: IAzureBenefitRequestsState,
  action: IGetAzureBenefitRequestDetailsSuccess
): IAzureBenefitRequestsState => ({
  ...state,
  selectedAzureBenefitRequest: action.questionAnswerMap,
  selectedAzureBenefitRequestIsExpedited: action.expedited,
  selectedAzureBenefitRequestExpeditedByDate: action?.expeditedDate,
});

const applyGetAzureBenefitRequestDetailsFail = (
  state: IAzureBenefitRequestsState,
  action: IGetAzureBenefitRequestDetailsFail
): IAzureBenefitRequestsState => ({
  ...state,
  selectedAzureBenefitRequest: undefined,
  fetchAzureBenefitRequestDetailsErrorMessage: action.error,
  selectedAzureBenefitRequestIsExpedited: false,
});

const applyApproveOrDeclineAzureBenefitRequest = (
  state: IAzureBenefitRequestsState,
  action: IApproveOrDeclineAzureBenefitRequest
): IAzureBenefitRequestsState => ({
  ...state,
  azureBenefitApproveOrDeclineOperationStatus: OperationStatus.Pending,
  azureBenefitApproveOrDeclineErrorMessage: "",
});

const applyApproveOrDeclineAzureBenefitRequestSuccess = (
  state: IAzureBenefitRequestsState,
  action: IApproveOrDeclineAzureBenefitRequestSuccess
): IAzureBenefitRequestsState => ({
  ...state,
  azureBenefitApproveOrDeclineOperationStatus: OperationStatus.Succeeded,
});

const applyApproveOrDeclineAzureBenefitRequestFailure = (
  state: IAzureBenefitRequestsState,
  action: IApproveOrDeclineAzureBenefitRequestFailure
): IAzureBenefitRequestsState => ({
  ...state,
  azureBenefitApproveOrDeclineOperationStatus: OperationStatus.Failed,
  azureBenefitApproveOrDeclineErrorMessage: action.errorMessage,
});

function azureBenefitRequestsReducer(
  state = INITIAL_STATE,
  action: AzureBenefitRequestsActionTypes
): IAzureBenefitRequestsState {
  switch (action.type) {
    case AZURE_BENEFIT_REQUESTS_SEARCH:
      return applySearchAzureBenefitRequests(state, action);
    case AZURE_BENEFIT_REQUESTS_SEARCH_FAIL:
      return applySearchAzureBenefitRequestsFail(state, action);
    case AZURE_BENEFIT_REQUESTS_SEARCH_SUCCESS:
      return applySearchAzureBenefitRequestsSuccess(state, action);
    case AZURE_BENEFIT_REQUESTS_FILTER_VALUE_ADD:
      return addAzureBenefitRequestsFilterValue(state, action);
    case AZURE_BENEFIT_REQUESTS_FILTER_VALUE_CLEAR:
      return clearAzureBenefitRequestsFilterValue(state);
    case AZURE_BENEFIT_REQUESTS_FILTER_VALUE_REMOVE:
      return removeAzureBenefitRequestsFilterValue(state, action);
    case AZURE_BENEFIT_REQUESTS_FILTER_VALUE_UPDATE:
      return updateAzureBenefitRequestsFilterValue(state, action);
    case GET_AZURE_BENEFIT_REQUEST_DETAILS:
      return applyGetAzureBenefitRequestDetails(state);
    case GET_AZURE_BENEFIT_REQUEST_DETAILS_FAIL:
      return applyGetAzureBenefitRequestDetailsFail(state, action);
    case GET_AZURE_BENEFIT_REQUEST_DETAILS_SUCCESS:
      return applyGetAzureBenefitRequestDetailsSuccess(state, action);
    case AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE:
      return applyApproveOrDeclineAzureBenefitRequest(state, action);
    case AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_FAILURE:
      return applyApproveOrDeclineAzureBenefitRequestFailure(state, action);
    case AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_SUCCESS:
      return applyApproveOrDeclineAzureBenefitRequestSuccess(state, action);
    default:
      return state;
  }
}

export default azureBenefitRequestsReducer;
